import { Paged } from "../interfaces/paged";
import {Injectable} from '@angular/core';
import {APIService} from "./api.service";
import {Delete, ListEntry} from "../interfaces/colDef";
import moment from "moment";
import { HttpClient } from "@angular/common/http";
import {environment as env} from "../../environments/environment";
import {NbToastrService} from "@nebular/theme";
import {Router} from "@angular/router";
import {AuthService} from "./auth.service";
import {Globals} from "./Globals";
import {BehaviorSubject, Subject} from "rxjs";
import {CookieService} from "ngx-cookie-service";
import {Address, ApprovalTrip, ReimbursementRate, TripRow, TripWeek} from "../interfaces/trip";

export enum TrackedStatus{
  Archived = 0,
  Complete = 1,
  Draft = 1,        // approval-workflow alias for the editable/unsubmitted state (== Complete)
  Tracking = 2,
  Paused = 3,
  Submitted = 4,
  Approved = 5,
  Rejected = 6
}

export interface TimeApprovalEvent {
  id: number;
  event_type: string;
  status_from?: TrackedStatus;
  status_to?: TrackedStatus;
  reason?: string;
  created_at: string;
  actor?: any;
  subject?: any;
}

export interface TimeApprovalRequest {
  id: number;
  Client_id: number;
  User_id: number;
  week_start: string;
  status: TrackedStatus;
  submitted_at?: string;
  decided_at?: string;
  reopened_at?: string;
  employee?: any;
  decided_by?: any;
  entries?: TrackedTime[];
  assignees?: any[];
  events?: TimeApprovalEvent[];
}

export interface TrackedTime{
  id: number,
  tracked_durationString?: string;
  description: string,
  User_to_Phase_id?: number,
  user_to_phase?: any,
  tracked_duration: number,
  tracked_start?: string,
  tracked_end?: string,
  tracked_on: string|Date,
  status: TrackedStatus,
  created_at: number
}

export interface TrackedWeek{
  User_to_Phase_id: number,
  description: string,
  duration: number,
  user_to_phase: any,
  weekday: number
}

@Injectable({
  providedIn: 'root'
})
export class TimeTrackingService extends APIService{

  private static trackinter = null;
  public readonly timer: BehaviorSubject<TrackedTime|null> = new BehaviorSubject<TrackedTime|null>(this.isTracking);

  // Fires whenever an approval decision changes the pending queue, so subscribers
  // (e.g. the sidebar badge) can re-fetch their count live instead of going stale
  // until the next navigation.
  public readonly approvalsChanged: Subject<void> = new Subject<void>();

  constructor(http: HttpClient,
              toast: NbToastrService,
              router: Router,
              cookie: CookieService,
              private auth: AuthService) {
    super(http, toast, router, cookie);
    this.setupTimer();
  }

  private setupTimer(){
    // Clear current
    if(TimeTrackingService.trackinter){
      TimeTrackingService.trackinter.clearInterval();
      TimeTrackingService.trackinter = null;
    }

    // setup to submit to subject
    let lasttrack = null;
    TimeTrackingService.trackinter = setInterval(() => {
      const t = this.isTracking;
      if(t || t !== lasttrack){
        this.timer.next(t);
      }
      lasttrack = t;
    }, 1000);
  }

  public async addTime(time: Partial<TrackedTime>, foruser: number = null): Promise<{tracking: TrackedTime, totals: any}> {
    const m = moment.duration(time.tracked_duration, 'seconds');
    return await this.post<Partial<TrackedTime>, {tracking: TrackedTime, totals: any}>('/time' + (foruser ? '/' + foruser : ''), time,  m.hours() + 'H ' + m.minutes() + 'M added!');
  }

  public async addBulkTime(time: Partial<TrackedTime>, users: number[]): Promise<{tracking: TrackedTime, totals: any}>{
    return await this.post<{track: Partial<TrackedTime>, users: number[]}, {tracking: TrackedTime, totals: any}>('/time/bulk', {track: time, users},
      users.length + ' track'+(users.length ! == 1 ? 's' : '')+' added!');
  }

  public get isTracking(): TrackedTime|null{
    // This is live so set our duration at the moment of retrieval
    const t = this.auth.user.getValue()?.tracking ?? null;
    if(t){
      const dur = this.span(t);
      t.tracked_duration = dur?.asSeconds();
      t.tracked_durationString = dur ? (dur.hours() + (Math.trunc(dur.asDays())*24)).toString().padStart(2, '0')
        + ':' + dur.minutes().toString().padStart(2, '0')
        + ':' + dur.seconds().toString().padStart(2, '0') : null;
    }
    return t;
  }

  async getTrackings(status: TrackedStatus = TrackedStatus.Complete, from: number = 0, foruser: number = null): Promise<{
    trackings: TrackedTime[],
    totals:any,
    capacities?: any,
    schedules?: any}>{
    return await this.get<TrackedTime[]>('/time' + (foruser ? '/' + foruser : '') , 'Could not get tracked times', {
      params: {
        status,
        from
      }
    });
  }

  async getWeek(date: string|Date, status: TrackedStatus = TrackedStatus.Complete, foruser: number = null,
                withError = 'Could not get tracked times')
    : Promise<{trackings: TrackedWeek[], totals: any, assignments: any[], approval_request?: TimeApprovalRequest}>{
    return await this.get<TrackedTime[]>('/week' + (foruser ? '/' + foruser : ''), withError, {
      params: {
        status,
        date
      }
    });
  }

  async getDates(status: TrackedStatus = TrackedStatus.Complete, date: Date = new Date()){
    return await this.get<{[key: string]: TrackedTime[]}>('/time/dates', 'Could not get your tracked times', {
      params: {
        status,
        date: Globals.toISODate(date)
      }
    });
  }

  async start(withinfo: Partial<TrackedTime> = {}): Promise<TrackedTime> {
    const user = this.auth.user.getValue();

    const now = moment();
    withinfo.created_at = (new Date()).getTime() / 1000;
    withinfo.tracked_start = now.format('HH:mm');
    withinfo.tracked_on = Globals.toISODate(now.toDate());
    withinfo.tracked_duration = 0;
    withinfo.tracked_end = null;
    withinfo.status = TrackedStatus.Tracking;

    const time = await this.post<typeof withinfo, {tracking: TrackedTime, totals:any}>('/time/start', withinfo, 'Tracking Started');
    if(time && user) {
      user.tracking = time.tracking;
    }
    this.timer.next(this.isTracking);
    return time.tracking;
  }

  async stop(withinfo: Partial<TrackedTime> = {}): Promise<TrackedTime>{
    const user = this.auth.user.getValue();
    const time = await this.post<typeof withinfo, {tracking: TrackedTime, totals:any}>('/time/stop', withinfo, 'Tracking Stopped');
    time.tracking.tracked_duration = this.span(time.tracking)?.asSeconds() ?? 0;
    user.tracking = null;
    this.timer.next(this.isTracking);
    return time.tracking;
  }

  private span(t: TrackedTime = this.isTracking): moment.Duration{
    if(t && t.created_at){
      const dur = moment.duration(moment().diff(moment(t.created_at)));
      if(dur.isValid()){
        return dur;
      }
    }
    return null;
  }

  public duration(t: number): {hours: number, minutes: number}|null{
    const dur = moment.duration(t, 'seconds');
    if(dur && dur.isValid()){
      return {hours: Math.floor(dur.asHours()), minutes: dur.minutes()};
    }
    return null;
  }

  async deleteTrack(track: TrackedTime): Promise<Delete & {totals: any}> {
    return await this.delete<Delete & {totals: any}>('/time/' + track.id);
  }

  async updateTime(id: number, time: Partial<TrackedTime>): Promise<{tracking: TrackedTime, totals:any}> {
    return await this.put<Partial<TrackedTime>, {tracking: TrackedTime, totals:any}>('/time/' + id, time);
  }

  async setCell(cell: {User_to_Phase_id: number|null, description: string|null, tracked_on: string, tracked_duration: number}, foruser: number = null): Promise<{totals: any}> {
    return await this.put<typeof cell, {totals: any}>('/time/cell' + (foruser ? '/' + foruser : ''), cell);
  }

  async relabelRow(row: {User_to_Phase_id: number|null, from_description: string|null, to_description: string|null, week_start: string}, foruser: number = null): Promise<{trackings: TrackedWeek[], totals: any}> {
    return await this.put<typeof row, {trackings: TrackedWeek[], totals: any}>('/time/row' + (foruser ? '/' + foruser : ''), row);
  }

  async removeRow(row: {User_to_Phase_id: number|null, description: string|null, week_start: string}, foruser: number = null): Promise<{trackings: TrackedWeek[], totals: any}> {
    return await this.post<typeof row, {trackings: TrackedWeek[], totals: any}>('/time/row/remove' + (foruser ? '/' + foruser : ''), row);
  }

  // Descriptions already used on the tenant's Internal activities project (#1512).
  async internalSuggestions(): Promise<string[]> {
    return await this.get<string[]>('/time/suggestions', 'Could not get description suggestions');
  }

  async submitWeek(date: string|Date, userId: number = null): Promise<{changed: boolean, message: string, request?: TimeApprovalRequest}> {
    const res = await this.post<any, {changed: boolean, message: string, request?: TimeApprovalRequest}>('/time/approval-requests/submit-week', {
      date: Globals.toISODate(date instanceof Date ? date : moment(date).toDate()),
      user_id: userId
    });
    // Surface the server's real message: success when actually changed, info otherwise
    // (avoids the lying "Week submitted" toast when the server says nothing changed).
    if (res) {
      if (res.changed) {
        this.toast.success(res.message || 'Week submitted', 'Success');
      } else {
        this.toast.info(res.message || 'No changes', 'Info');
      }
    }
    return res;
  }

  async pendingApprovalCount(): Promise<{count: number}> {
    return await this.get<{count: number}>('/time/approval-requests/count', 'Could not get pending approvals');
  }

  async listApprovals(page: number = 1, filters: {status?: number|string, user_id?: number, week_start?: string, department_id?: number, project_id?: number} = {}, per_page: number = 25)
    : Promise<{requests: Paged<TimeApprovalRequest>, options: {employees: {id: number, name: string}[], teams: {id: number, name: string}[], projects: {id: number, name: string}[]}}> {
    const params: any = { page, per_page };
    if (filters.status !== undefined && filters.status !== null && filters.status !== '') { params.status = filters.status; }
    if (filters.user_id) { params.user_id = filters.user_id; }
    if (filters.week_start) { params.week_start = filters.week_start; }
    if (filters.department_id) { params.department_id = filters.department_id; }
    if (filters.project_id) { params.project_id = filters.project_id; }
    return await this.get('/time/approval-requests', 'Could not get approvals', { params });
  }

  // Best-effort deep-link lookup: a miss (paginated off-page, wrong tenant, not
  // reviewable, or gone) must stay a silent no-op. Bypasses the shared get()/
  // errorify() path deliberately — its 401 handling redirects the whole app to
  // /login, which is right for session expiry but wrong for "this one row isn't
  // visible to you".
  async getApproval(id: number): Promise<TimeApprovalRequest | null> {
    return this.http.get<TimeApprovalRequest>(env.api + '/time/approval-requests/' + id, this.apiOptions)
      .toPromise()
      .catch(() => null);
  }

  async approveRequest(id: number): Promise<TimeApprovalRequest> {
    const r = await this.post<any, TimeApprovalRequest>('/time/approval-requests/' + id + '/approve', {}, 'Week approved');
    this.approvalsChanged.next();
    return r;
  }

  async rejectRequest(id: number, reason: string): Promise<TimeApprovalRequest> {
    const r = await this.post<any, TimeApprovalRequest>('/time/approval-requests/' + id + '/reject', {reason}, 'Week rejected');
    this.approvalsChanged.next();
    return r;
  }

  async recallRequest(id: number): Promise<TimeApprovalRequest> {
    const r = await this.post<any, TimeApprovalRequest>('/time/approval-requests/' + id + '/recall', {}, 'Submission withdrawn');
    this.approvalsChanged.next();
    return r;
  }

  async reopenRequest(id: number, reason: string, locale: string): Promise<TimeApprovalRequest> {
    const r = await this.post<any, TimeApprovalRequest>('/time/approval-requests/' + id + '/reopen', {reason, locale}, 'Week reopened');
    this.approvalsChanged.next();
    return r;
  }

  // Trips (#874): rows per route and project with km per weekday, locked with the week.
  async getTrips(date: string, userId: number = null): Promise<TripWeek> {
    return await this.get<TripWeek>('/trips/week', 'Could not get trips', {params: userId ? {date, user_id: userId} : {date}});
  }

  async addTripRow(row: TripRow & {date: string, return: boolean}): Promise<TripWeek> {
    return await this.post<typeof row, TripWeek>('/trips/row', row, 'Trip added');
  }

  async updateTripRow(row: TripRow): Promise<TripWeek> {
    return await this.put<TripRow, TripWeek>('/trips/row', row, 'Trip saved');
  }

  async setTripCell(cell: {row_id: string, date: string, km: number | null}): Promise<TripWeek> {
    return await this.put<typeof cell, TripWeek>('/trips/cell', cell);
  }

  async removeTripRow(rowId: string): Promise<TripWeek> {
    return await this.post<{row_id: string}, TripWeek>('/trips/row/remove', {row_id: rowId}, 'Trip removed');
  }

  // The week holding date gets last week's routes and km; the caller reports how many trip days came over.
  async copyTripWeek(date: string): Promise<TripWeek> {
    return await this.post<{date: string}, TripWeek>('/trips/copy-week', {date});
  }

  async downloadTripOverview(from: Date, to: Date, userId: number, format: string): Promise<void> {
    const params = {from: Globals.toISODate(from), to: Globals.toISODate(to), ...(userId ? {user_id: userId} : {})};
    const response = await this.http.get(env.api + '/trips/overview/' + format, {...this.apiOptions, params, responseType: 'blob' as const, observe: 'response' as const})
      .toPromise()
      // A failed blob request has a Blob body too; read it so a 422 shows its own message.
      .catch(async e => {
        const message = await e.error?.text?.().then((t: string) => JSON.parse(t).message).catch(() => null);
        return this.errorify(Promise.reject(e), message || 'Could not create the trip overview');
      });
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(response.body);
    a.download = response.headers.get('Content-Disposition')?.match(/filename="?([^";]+)"?/)?.[1] || 'trips.' + format;
    a.click();
    window.URL.revokeObjectURL(a.href);
  }

  async getHomeAddress(): Promise<Partial<Address>> {
    return await this.get<Partial<Address>>('/trips/home-address', 'Could not get your home address');
  }

  async saveHomeAddress(address: Partial<Address>): Promise<Partial<Address>> {
    return await this.put<Partial<Address>, Partial<Address>>('/trips/home-address', address, 'Home address saved');
  }

  async getReimbursementRates(): Promise<ReimbursementRate[]> {
    return await this.get<ReimbursementRate[]>('/trips/reimbursement-rates', 'Could not get the reimbursement rates');
  }

  async saveReimbursementRate(rate: ReimbursementRate): Promise<ReimbursementRate> {
    return rate.id
      ? await this.put<ReimbursementRate, ReimbursementRate>('/trips/reimbursement-rates/' + rate.id, rate, 'Reimbursement rate saved')
      : await this.post<ReimbursementRate, ReimbursementRate>('/trips/reimbursement-rates', rate, 'Reimbursement rate saved');
  }

  async removeReimbursementRate(id: number): Promise<{deleted: boolean}> {
    return await this.delete<{deleted: boolean}>('/trips/reimbursement-rates/' + id, 'Reimbursement rate deleted');
  }

  // Silent like getApproval(): a Staffer who may view the week may not see its trips, and a
  // tenant without trips answers 404; neither is an error to show.
  async getApprovalTrips(id: number): Promise<ApprovalTrip[]> {
    return this.http.get<ApprovalTrip[]>(env.api + '/time/approval-requests/' + id + '/trips', this.apiOptions)
      .toPromise()
      .catch(() => []);
  }

  async exportTimesheet(start: Date, end: Date, foruser: number = null, format: string = 'xlsx'): Promise<void> {
    const startStr = Globals.toISODate(start);
    const endStr = Globals.toISODate(end);
    const url = '/time/export' + (foruser ? '/' + foruser : '') + `?start=${startStr}&end=${endStr}&format=${format}`;

    const response = await this.get(url, null, {
      responseType: 'blob',
      observe: 'response',
      withCredentials: true
    });

    const contentDisposition = response.headers.get('Content-Disposition');
    const filename = contentDisposition?.match(/filename=([^;]+)/)?.[1] || 'timesheet.csv';

    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(response.body);
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(a.href);
  }
}
